import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSEO } from '@/hooks/useSEO';

const VALUES = [
  {
    icon: 'ri-focus-3-line',
    title: '内容聚焦',
    desc: '专注电视剧与电影的剧情、演员与作品资料，提供清晰专业的内容浏览体验。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '合规纯净',
    desc: '本站不提供在线播放与下载功能，仅做影视资料展示，尊重版权与创作。',
  },
  {
    icon: 'ri-user-smile-line',
    title: '体验优先',
    desc: '多维度筛选、快速搜索与清晰的详情页，帮助你在最短时间内找到想看的内容。',
  },
];

const FAQS = [
  {
    q: '本站可以观看或下载影视资源吗？',
    a: '不可以。年轻的岳母是一个影视资料展示平台，仅提供剧情简介、演员信息与作品分类等信息浏览功能，不涉及任何在线播放、下载或支付接口。',
  },
  {
    q: '如何找到感兴趣的影视作品？',
    a: '你可以通过顶部搜索框输入剧名、演员或导演进行搜索，也可以进入电视剧频道或电影频道，按类型、地区、年份进行多维度筛选，并按更新时间或热度排序。',
  },
  {
    q: '收藏功能会保存我的数据吗？',
    a: '收藏功能仅为本地演示用途，不涉及账号系统与后端存储，页面刷新后收藏状态会自动重置。',
  },
  {
    q: '网站内容多久更新一次？',
    a: '演示站点中的影视资料为预置示例数据，实际运营时可由内容团队持续维护更新，以最新更新时间为准。',
  },
  {
    q: '如何与我们联系或反馈问题？',
    a: '你可以通过下方邮箱与我们取得联系，或关注官方社交账号获取最新资讯。我们欢迎任何关于内容与体验的建议。',
  },
];

export default function AboutPage() {
  const [open, setOpen] = useState<number | null>(0);

  useSEO({
    title: '关于我们 - 年轻的岳母影视平台',
    description:
      '了解年轻的岳母影视平台：我们专注高清电视剧与电影的资料展示，提供剧情介绍与演员信息。查看品牌介绍、免责声明、版权声明与联系方式。',
    keywords: '年轻的岳母,关于我们,影视平台,免责声明,版权声明',
  });

  return (
    <Layout>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20warm%20editorial%20scene%20of%20a%20modern%20home%20cinema%20room%20with%20soft%20amber%20projector%20light%20and%20deep%20blue%20shadows%2C%20cozy%20atmospheric%20film%20still%2C%20high%20detail&width=1600&height=600&seq=about-hero&orientation=landscape"
            alt="年轻的岳母影视平台"
            title="年轻的岳母 品牌介绍背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/80 to-secondary-950/50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-16 md:pb-20">
          <Breadcrumb tone="inverted" items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-bold text-background-50 max-w-2xl leading-tight">
            我们记录每一部值得了解的好作品
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-background-200 leading-relaxed">
            年轻的岳母是一个面向影视爱好者的高清影视资料展示平台。我们希望用清晰的结构、专业的资料与舒适的浏览体验，帮助你在追剧之前先读懂一部作品。
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {VALUES.map((v) => (
            <div key={v.title} className="p-6 rounded-lg bg-background-50 border border-background-200">
              <span className="w-12 h-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center text-2xl">
                <i className={v.icon} aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Statements + Contact */}
      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 rounded-lg bg-background-50 border border-background-200">
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-error-warning-line text-accent-600" aria-hidden="true"></i>
                免责声明
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                本站所展示的影视信息、剧情简介及海报图片均为演示用途与网络公开资料整理，仅供参考。本站不提供任何影音播放、下载或存储服务，不托管任何影视资源文件。若相关内容涉及版权问题，请权利人通过下方联系方式与我们沟通，我们将及时处理。
              </p>
            </div>
            <div className="p-6 rounded-lg bg-background-50 border border-background-200">
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-copyright-line text-accent-600" aria-hidden="true"></i>
                版权声明
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                本站的栏目设置、页面设计及原创文案的版权归年轻的岳母所有，未经许可不得用于商业用途。站内引用的影视作品名称、演职人员信息等归各自权利人所有。我们鼓励用户通过正规渠道支持正版影视内容。
              </p>
            </div>
          </div>

          <div className="p-6 rounded-lg bg-background-50 border border-background-200 h-fit">
            <h2 className="font-heading text-lg font-bold text-foreground-950">联系我们</h2>
            <ul className="mt-4 space-y-4 text-sm">
              <li>
                <a
                  href="mailto:contact@ylws-cinema.demo"
                  className="flex items-center gap-3 text-foreground-700 hover:text-primary-700 transition-colors"
                >
                  <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-primary-600">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  contact@ylws-cinema.demo
                </a>
              </li>
              <li>
                <a
                  href="https://weibo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-foreground-700 hover:text-primary-700 transition-colors"
                >
                  <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-primary-600">
                    <i className="ri-weibo-line" aria-hidden="true"></i>
                  </span>
                  微博 · @年轻的岳母
                </a>
              </li>
              <li>
                <a
                  href="https://www.wechat.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-foreground-700 hover:text-primary-700 transition-colors"
                >
                  <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-primary-600">
                    <i className="ri-wechat-line" aria-hidden="true"></i>
                  </span>
                  微信公众号 · 年轻的岳母影视
                </a>
              </li>
            </ul>
            <div className="mt-5 pt-5 border-t border-background-200 flex flex-wrap gap-3">
              <Link
                to="/category/tv"
                className="px-4 h-9 inline-flex items-center rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                电视剧频道
              </Link>
              <Link
                to="/category/movie"
                className="px-4 h-9 inline-flex items-center rounded-md bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                电影频道
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">常见问题</h2>
          <p className="mt-2 text-sm text-foreground-500">关于平台的使用与说明</p>
        </div>
        <div className="space-y-3">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm md:text-base font-medium text-foreground-900">{item.q}</span>
                  <i
                    className={`shrink-0 text-xl text-primary-600 transition-transform ${
                      isOpen ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {isOpen && (
                  <p className="px-5 pb-5 -mt-1 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}